import {
  AppWindow,
  ArrowDown,
  ArrowRight,
  BadgeCheck,
  Box,
  ChevronRight,
  CircuitBoard,
  Cpu,
  Hammer,
  LayoutGrid,
  MonitorPlay,
  Package,
  Palette,
  PenTool,
  Power,
  Rocket,
  Search,
  Smartphone,
} from "lucide-react";
import { RevealSection, revealClass, useReveal } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import megogoImg from "@/assets/customization/megogo.jpg";
import kyivstarImg from "@/assets/customization/kyivstar.jpg";
import sweetTvImg from "@/assets/customization/sweet-tv.jpg";
import almaTvImg from "@/assets/customization/alma-tv.jpg";
import beelineImg from "@/assets/customization/beeline.jpg";
import katv1Img from "@/assets/customization/katv1.jpg";
import sarkorImg from "@/assets/customization/sarkor.jpg";
import metroportImg from "@/assets/customization/metroport.jpg";
import softwareLauncherImg from "@/assets/customization/software-launcher.jpg";
import hardwareKitImg from "@/assets/customization/hardware-kit.jpg";
import heroEngineeringImg from "@/assets/customization/hero-engineering.jpg";

const SPECTRUM = "var(--gradient-spectrum)";

export function CustomizationPage() {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const salesCta = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const demoCta = localizeUrl("/contacts?inquiry=demo#inquiry-form", site.locale, href);
  const alcatraz = localizeUrl("/solutions/alcatraz-dms", site.locale, href);

  const heroStats = [
    messages.customPageHeroStat1,
    messages.customPageHeroStat2,
    messages.customPageHeroStat3,
  ];

  const tiers = [
    {
      name: messages.customPageTier1Name,
      body: messages.customPageTier1Body,
      tag: messages.customPageTier1Tag,
    },
    {
      name: messages.customPageTier2Name,
      body: messages.customPageTier2Body,
      tag: messages.customPageTier2Tag,
    },
    {
      name: messages.customPageTier3Name,
      body: messages.customPageTier3Body,
      tag: messages.customPageTier3Tag,
    },
    {
      name: messages.customPageTier4Name,
      body: messages.customPageTier4Body,
      tag: messages.customPageTier4Tag,
    },
  ];

  const pillars = [
    { icon: Package, title: messages.customPagePillar1Title, body: messages.customPagePillar1Body },
    { icon: Power, title: messages.customPagePillar2Title, body: messages.customPagePillar2Body },
    {
      icon: LayoutGrid,
      title: messages.customPagePillar3Title,
      body: messages.customPagePillar3Body,
    },
    {
      icon: MonitorPlay,
      title: messages.customPagePillar4Title,
      body: messages.customPagePillar4Body,
    },
    {
      icon: AppWindow,
      title: messages.customPagePillar5Title,
      body: messages.customPagePillar5Body,
    },
    {
      icon: Smartphone,
      title: messages.customPagePillar6Title,
      body: messages.customPagePillar6Body,
    },
    { icon: Cpu, title: messages.customPagePillar7Title, body: messages.customPagePillar7Body },
    {
      icon: CircuitBoard,
      title: messages.customPagePillar8Title,
      body: messages.customPagePillar8Body,
    },
  ];

  const examples = [
    {
      image: megogoImg,
      name: messages.customPageExample1Name,
      tag: messages.customPageExample1Tag,
      meta: messages.customPageExample1Meta,
      body: messages.customPageExample1Body,
    },
    {
      image: kyivstarImg,
      name: messages.customPageExample2Name,
      tag: messages.customPageExample2Tag,
      meta: messages.customPageExample2Meta,
      body: messages.customPageExample2Body,
    },
    {
      image: sweetTvImg,
      name: messages.customPageExample3Name,
      tag: messages.customPageExample3Tag,
      meta: messages.customPageExample3Meta,
      body: messages.customPageExample3Body,
    },
    {
      image: almaTvImg,
      name: messages.customPageExample4Name,
      tag: messages.customPageExample4Tag,
      meta: messages.customPageExample4Meta,
      body: messages.customPageExample4Body,
    },
    {
      image: metroportImg,
      name: messages.customPageExample8Name,
      tag: messages.customPageExample8Tag,
      meta: messages.customPageExample8Meta,
      body: messages.customPageExample8Body,
    },
    {
      image: beelineImg,
      name: messages.customPageExample5Name,
      tag: messages.customPageExample5Tag,
      meta: messages.customPageExample5Meta,
      body: messages.customPageExample5Body,
    },
    {
      image: katv1Img,
      name: messages.customPageExample6Name,
      tag: messages.customPageExample6Tag,
      meta: messages.customPageExample6Meta,
      body: messages.customPageExample6Body,
    },
    {
      image: sarkorImg,
      name: messages.customPageExample7Name,
      tag: messages.customPageExample7Tag,
      meta: messages.customPageExample7Meta,
      body: messages.customPageExample7Body,
    },
  ];

  const steps = [
    {
      icon: Search,
      title: messages.customPageProcessStep1Title,
      body: messages.customPageProcessStep1Body,
    },
    {
      icon: PenTool,
      title: messages.customPageProcessStep2Title,
      body: messages.customPageProcessStep2Body,
    },
    {
      icon: Hammer,
      title: messages.customPageProcessStep3Title,
      body: messages.customPageProcessStep3Body,
    },
    {
      icon: BadgeCheck,
      title: messages.customPageProcessStep4Title,
      body: messages.customPageProcessStep4Body,
    },
    {
      icon: Rocket,
      title: messages.customPageProcessStep5Title,
      body: messages.customPageProcessStep5Body,
    },
  ];

  const faqs = [
    { q: messages.customPageFaqQ1, a: messages.customPageFaqA1 },
    { q: messages.customPageFaqQ2, a: messages.customPageFaqA2 },
    { q: messages.customPageFaqQ3, a: messages.customPageFaqA3 },
    { q: messages.customPageFaqQ4, a: messages.customPageFaqA4 },
    { q: messages.customPageFaqQ5, a: messages.customPageFaqA5 },
    { q: messages.customPageFaqQ6, a: messages.customPageFaqA6 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.customPageBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/solutions", site.locale, href)} className="hover:text-foreground">
            {messages.customPageBreadcrumbSolutions}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.customPageBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-50"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-70"
          style={{ background: "var(--gradient-spectrum-soft)" }}
        />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-24">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: SPECTRUM }} />
              {messages.customPageHeroEyebrow}
            </div>
            <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-5xl">
              {messages.customPageHeroTitle}{" "}
              <span className="text-spectrum">{messages.customPageHeroTitleAccent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              {messages.customPageHeroSub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="animate-glow">
                <a href={salesCta}>{messages.customPageHeroCtaPrimary}</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#examples">
                  {messages.customPageHeroCtaSecondary}
                  <ArrowDown className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {heroStats.map((label) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: SPECTRUM }} />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="animate-fade-up">
            <figure className="relative">
              <div
                aria-hidden
                className="absolute -inset-6 -z-10 rounded-[2rem] opacity-70 blur-3xl"
                style={{ background: "var(--gradient-spectrum-soft)" }}
              />
              <img
                src={heroEngineeringImg}
                alt={messages.customPageHeroImageAlt}
                loading="eager"
                className="w-full rounded-2xl border border-border/60 shadow-2xl"
              />
            </figure>
          </div>
        </div>
      </section>

      {/* Two surfaces — software + hardware */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{messages.customPageSurfacesEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.customPageSurfacesTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              {messages.customPageSurfacesDescription}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            <SurfaceCard
              icon={Smartphone}
              tag={messages.customPageSoftwareTag}
              title={messages.customPageSoftwareTitle}
              body={messages.customPageSoftwareBody}
              bullets={[
                messages.customPageSoftwareBullet1,
                messages.customPageSoftwareBullet2,
                messages.customPageSoftwareBullet3,
              ]}
            />
            <SurfaceCard
              icon={Box}
              tag={messages.customPageHardwareTag}
              title={messages.customPageHardwareTitle}
              body={messages.customPageHardwareBody}
              bullets={[
                messages.customPageHardwareBullet1,
                messages.customPageHardwareBullet2,
                messages.customPageHardwareBullet3,
              ]}
            />
          </div>
        </div>
      </RevealSection>

      {/* Tier ladder — branding of any level */}
      <RevealSection className="border-b border-border/60 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{messages.customPageTiersEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.customPageTiersTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              {messages.customPageTiersDescription}
            </p>
          </div>

          <ol className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {tiers.map((tier, i) => (
              <li
                key={tier.name}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur transition-colors hover:border-primary/30"
              >
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1"
                  style={{ background: SPECTRUM, opacity: 0.35 + i * 0.22 }}
                />
                <div className="flex items-center justify-between">
                  <DepthBars level={i + 1} />
                  <span className="rounded-full border border-border/60 bg-background/70 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    {tier.tag}
                  </span>
                </div>
                <p className="mt-4 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {messages.customPageTiersLevelLabel} {i + 1}
                </p>
                <h3 className="mt-1 text-base font-semibold text-foreground">{tier.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tier.body}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {messages.customPageTiersFootnote}
          </p>
        </div>
      </RevealSection>

      {/* Pillars — what you can customize */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{messages.customPagePillarsEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.customPagePillarsTitle}
            </h2>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="rounded-2xl border border-border/60 bg-card/80 p-5 backdrop-blur transition-colors hover:border-primary/30"
              >
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm"
                  style={{ background: SPECTRUM }}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* Software deep-dive */}
      <DeepDive
        layout="imageRight"
        image={softwareLauncherImg}
        eyebrow={messages.customPageSoftwareDeepEyebrow}
        title={messages.customPageSoftwareDeepTitle}
        body={messages.customPageSoftwareDeepBody}
        bullets={[
          messages.customPageSoftwareDeepBullet1,
          messages.customPageSoftwareDeepBullet2,
          messages.customPageSoftwareDeepBullet3,
          messages.customPageSoftwareDeepBullet4,
        ]}
        placeholderLabel={messages.customPageSoftwareDeepPlaceholder}
        placeholderIcon={Palette}
      />

      {/* Hardware deep-dive */}
      <DeepDive
        layout="imageLeft"
        muted
        image={hardwareKitImg}
        eyebrow={messages.customPageHardwareDeepEyebrow}
        title={messages.customPageHardwareDeepTitle}
        body={messages.customPageHardwareDeepBody}
        bullets={[
          messages.customPageHardwareDeepBullet1,
          messages.customPageHardwareDeepBullet2,
          messages.customPageHardwareDeepBullet3,
          messages.customPageHardwareDeepBullet4,
        ]}
        placeholderLabel={messages.customPageHardwareDeepPlaceholder}
        placeholderIcon={CircuitBoard}
      />

      {/* Examples gallery */}
      <RevealSection id="examples" className="scroll-mt-20 border-b border-border/60 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{messages.customPageExamplesEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.customPageExamplesTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              {messages.customPageExamplesDescription}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {examples.map(({ image, name, tag, meta, body }) => (
              <article
                key={name}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/60 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-glow"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#f3f3f5]">
                  <img
                    src={image}
                    alt={name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                  />
                  <span
                    className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-white shadow-sm"
                    style={{ background: SPECTRUM }}
                  >
                    {tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{name}</h3>
                    <span className="shrink-0 text-[11px] text-muted-foreground/70">{meta}</span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Process stepper */}
      <RevealSection className="border-b border-border/60 bg-muted/20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{messages.customPageProcessEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.customPageProcessTitle}
            </h2>
          </div>

          <ol className="relative mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
            <span
              aria-hidden
              className="pointer-events-none absolute left-0 right-0 top-5 hidden h-px lg:block"
              style={{ background: SPECTRUM, opacity: 0.4 }}
            />
            {steps.map(({ icon: Icon, title, body }, i) => (
              <li key={title} className="relative flex flex-col">
                <span
                  className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full text-white shadow-md ring-4 ring-muted/20"
                  style={{ background: SPECTRUM }}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <p className="mt-4 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {messages.customPageTiersLevelLabel} {i + 1}
                </p>
                <h3 className="mt-1 text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ol>

          <div className="mt-10 flex justify-center">
            <a
              href={alcatraz}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-foreground"
            >
              {messages.navSolutionsAlcatraz}
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <Eyebrow>{messages.customPageFaqEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.customPageFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-20 text-background">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-40"
          style={{ background: SPECTRUM }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.customPageFinalEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.customPageFinalTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.customPageFinalBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary">
              <a href={salesCta}>{messages.customPageFinalCta}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
            >
              <a href={demoCta}>{messages.customPageFinalCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: SPECTRUM }} />
      {children}
    </span>
  );
}

/** Ascending "depth" bars (1–4 filled) for the tier ladder. */
function DepthBars({ level }: { level: number }) {
  return (
    <span aria-hidden className="flex items-end gap-1">
      {[1, 2, 3, 4].map((n) => (
        <span
          key={n}
          className="w-1.5 rounded-full"
          style={{
            height: 6 + n * 4,
            background: n <= level ? SPECTRUM : "var(--color-border)",
            opacity: n <= level ? 1 : 0.8,
          }}
        />
      ))}
    </span>
  );
}

function SurfaceCard({
  icon: Icon,
  tag,
  title,
  body,
  bullets,
}: {
  icon: typeof Smartphone;
  tag: string;
  title: string;
  body: string;
  bullets: string[];
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/80 p-6 backdrop-blur">
      <span aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: SPECTRUM }} />
      <div className="flex items-start justify-between">
        <span
          className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm"
          style={{ background: SPECTRUM }}
        >
          <Icon className="h-5 w-5" />
        </span>
        <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {tag}
        </span>
      </div>
      <h3 className="mt-5 text-xl font-semibold tracking-tight text-foreground">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      <ul className="mt-5 space-y-2 text-sm text-foreground/90">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeepDive({
  eyebrow,
  title,
  body,
  bullets,
  image,
  placeholderLabel,
  placeholderIcon: PlaceholderIcon,
  layout,
  muted,
}: {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  image?: string;
  placeholderLabel: string;
  placeholderIcon: typeof Palette;
  layout: "imageLeft" | "imageRight";
  muted?: boolean;
}) {
  const imageLeft = layout === "imageLeft";
  const { ref, shown } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`${revealClass(shown)} border-b border-border/60 py-20 ${
        muted ? "bg-muted/20" : "bg-background"
      }`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
        <div className={imageLeft ? "order-2 lg:order-1" : "order-2"}>
          <figure className="relative">
            <div
              aria-hidden
              className="absolute -inset-4 -z-10 rounded-3xl opacity-60 blur-2xl"
              style={{ background: "var(--gradient-spectrum-soft)" }}
            />
            <div className="rounded-2xl border border-border/60 bg-card/80 p-2 shadow-xl backdrop-blur">
              {image ? (
                <img
                  src={image}
                  alt={title}
                  loading="lazy"
                  className="aspect-[16/10] w-full rounded-xl object-cover"
                />
              ) : (
                <div className="flex aspect-[16/10] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border/60 bg-muted/30 p-6 text-center">
                  <PlaceholderIcon className="h-10 w-10 text-muted-foreground/40" />
                  <p className="text-xs text-muted-foreground/60">{placeholderLabel}</p>
                </div>
              )}
            </div>
          </figure>
        </div>
        <div className={imageLeft ? "order-1 lg:order-2" : "order-1"}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">{body}</p>
          <ul className="mt-6 space-y-2 text-sm text-foreground/90">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
